import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/AdminLayout';
import { PROPERTIES } from '@/data/index';
import { ROUTE_PATHS, type Property, type PropertyType, type PropertyStatus } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Search, Edit, Trash2, Image as ImageIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export default function AdminProperties() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    filterProperties();
  }, [properties, searchQuery, typeFilter, statusFilter]);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('properties_ave')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        const dbProperties: Property[] = data.map((item: any) => ({
          id: item.id,
          title: item.title,
          type: item.type as PropertyType,
          category: item.category || undefined,
          price: item.price,
          location: item.location,
          description: item.description,
          features: item.features || [],
          images: item.images || [],
          status: item.status as PropertyStatus,
          featured: item.featured || false,
          badge: item.badge || undefined,
        }));
        setProperties(dbProperties);
      } else {
        setProperties(PROPERTIES);
      }
    } catch (error) {
      console.error('Error loading properties:', error);
      setProperties(PROPERTIES);
    } finally {
      setLoading(false);
    }
  };

  const filterProperties = () => {
    let filtered = [...properties];

    if (searchQuery) {
      filtered = filtered.filter((p) =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter((p) => p.type === typeFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((p) => p.status === statusFilter);
    }

    setFilteredProperties(filtered);
  };

  const handleToggleFeatured = async (id: string, currentFeatured: boolean) => {
    try {
      const { error } = await supabase
        .from('properties_ave')
        .update({ featured: !currentFeatured })
        .eq('id', id);

      if (error) throw error;

      setProperties((prev) =>
        prev.map((p) => (p.id === id ? { ...p, featured: !currentFeatured } : p))
      );
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const handleDeleteClick = (id: string) => {
    setPropertyToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!propertyToDelete) return;

    try {
      const { error } = await supabase
        .from('properties_ave')
        .delete()
        .eq('id', propertyToDelete);

      if (error) throw error;

      setProperties((prev) => prev.filter((p) => p.id !== propertyToDelete));
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
    } catch (error) {
      console.error('Error deleting property:', error);
    }
  };

  const getTypeBadgeColor = (type: PropertyType) => {
    switch (type) {
      case 'Plot':
        return 'bg-secondary text-secondary-foreground';
      case 'Villa':
        return 'bg-accent text-accent-foreground';
      case 'Condominium':
        return 'bg-primary text-primary-foreground';
      case 'Project':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusBadgeColor = (status: PropertyStatus) => {
    switch (status) {
      case 'Available':
        return 'bg-secondary text-secondary-foreground';
      case 'Sold':
        return 'bg-destructive text-destructive-foreground';
      case 'Reserved':
        return 'bg-accent text-accent-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Properties</h1>
            <p className="text-muted-foreground mt-1">
              Manage your property listings
            </p>
          </div>
          <Button
            onClick={() => navigate(ROUTE_PATHS.ADMIN_PROPERTIES_NEW)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add New Property
          </Button>
        </div>

        <Card className="p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Plot">Plot</SelectItem>
                <SelectItem value="Villa">Villa</SelectItem>
                <SelectItem value="Condominium">Condominium</SelectItem>
                <SelectItem value="Project">Project</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Available">Available</SelectItem>
                <SelectItem value="Sold">Sold</SelectItem>
                <SelectItem value="Reserved">Reserved</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              Loading properties...
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                {searchQuery || typeFilter !== 'all' || statusFilter !== 'all'
                  ? 'No properties match your filters'
                  : 'No properties found'}
              </p>
              {!searchQuery && typeFilter === 'all' && statusFilter === 'all' && (
                <Button
                  onClick={() => navigate(ROUTE_PATHS.ADMIN_PROPERTIES_NEW)}
                  variant="outline"
                >
                  Add Your First Property
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {filteredProperties.length} {filteredProperties.length === 1 ? 'property' : 'properties'} found
              </p>
              <div className="grid gap-4">
                {filteredProperties.map((property) => (
                  <Card key={property.id} className="p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="w-full md:w-32 h-32 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                        {property.images && property.images.length > 0 ? (
                          <img
                            src={property.images[0]}
                            alt={property.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-foreground truncate">
                              {property.title}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {property.location}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                navigate(
                                  ROUTE_PATHS.ADMIN_PROPERTIES_EDIT.replace(
                                    ':id',
                                    property.id
                                  )
                                )
                              }
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteClick(property.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <Badge className={getTypeBadgeColor(property.type)}>
                            {property.type}
                          </Badge>
                          <Badge className={getStatusBadgeColor(property.status)}>
                            {property.status}
                          </Badge>
                          {property.badge && (
                            <Badge variant="outline">{property.badge}</Badge>
                          )}
                          <span className="text-lg font-mono font-semibold text-accent">
                            ${property.price.toLocaleString()}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <Switch
                            checked={property.featured}
                            onCheckedChange={() =>
                              handleToggleFeatured(property.id, property.featured)
                            }
                          />
                          <span className="text-sm text-muted-foreground">
                            Featured Property
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this property? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}